import { alpha, createTheme } from '@mui/material/styles';

// Design tokens for the portal's dark theme (HOME-208), matched to the live
// sandbox app (sandboxsite/sandbox-app) — the same MUI stack and the real
// reference for the Nakomis look. These are its *checked-in* values, not
// eyeballed: ground #282c34, bars/footer #1f2329, blue.A700 accent. MUI's JS
// colour engine only parses hex/rgb(a)/hsl(a) (never oklch), so plain hex.
const tokens = {
    background: '#282c34', // blue-tinted charcoal ground (sandbox body + App-header)
    foreground: '#f4f4f5', // near-white body text
    bar: '#1f2329', // AppBar + Footer — one step darker than the ground
    card: '#2f333b', // panel, one step lighter than the ground (tables/cards)
    cardForeground: '#f4f4f5',
    primary: '#2962ff', // MUI blue.A700 — tab underline + CTA buttons
    primaryForeground: '#ffffff', // white text on the blue button
    secondary: '#3a3f4a', // neutral chip/secondary surface
    secondaryForeground: '#f4f4f5',
    muted: '#3a3f4a',
    mutedForeground: '#585c64', // muted text — exact sandbox value
    destructive: '#ff6467', // coral red (kept)
    border: 'rgba(255, 255, 255, 0.1)', // subtle neutral hairline
};

// Status colours (up/down/pending/etc.) are functional, not brand colour —
// Nakostat has no equivalent UI, so these keep the portal's existing hues
// but are restyled into Nakostat's "soft badge" idiom: a tinted background
// at low opacity plus solid text, rather than a solid block of colour.
const success = '#4caf50';
const warning = '#ffb74d';
const info = '#5c6bc0';

// --radius in Nakostat is 0.625rem (10px); shadcn's Card uses --radius-xl,
// i.e. --radius * 1.4 = 14px.
const radius = 10;
const cardRadius = 14;

export const theme = createTheme({
    palette: {
        mode: 'dark',
        background: { default: tokens.background, paper: tokens.card },
        text: { primary: tokens.foreground, secondary: tokens.mutedForeground },
        primary: { main: tokens.primary, contrastText: tokens.primaryForeground },
        secondary: { main: tokens.secondary, contrastText: tokens.secondaryForeground },
        error: { main: tokens.destructive },
        success: { main: success },
        warning: { main: warning },
        info: { main: info },
        divider: tokens.border,
    },
    shape: { borderRadius: radius },
    typography: {
        // Geist Variable, matching Nakostat; falls back to the platform sans
        // stack if the webfont hasn't loaded.
        fontFamily: [
            'Geist Variable',
            '-apple-system',
            'BlinkMacSystemFont',
            'Segoe UI',
            'Roboto',
            'Helvetica',
            'Arial',
            'sans-serif',
        ].join(','),
        button: { textTransform: 'none', fontWeight: 500 },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: { backgroundColor: tokens.background, color: tokens.foreground },
            },
        },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    border: `1px solid ${tokens.border}`,
                    borderRadius: cardRadius,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: tokens.bar,
                    backgroundImage: 'none',
                    borderBottom: `1px solid ${tokens.border}`,
                    boxShadow: 'none',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: radius - 2, fontWeight: 500 },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: 9999, fontWeight: 500 },
                colorSuccess: { backgroundColor: alpha(success, 0.16), color: success },
                colorWarning: { backgroundColor: alpha(warning, 0.18), color: warning },
                colorInfo: { backgroundColor: alpha(info, 0.2), color: '#c5cae9' },
                colorError: { backgroundColor: alpha(tokens.destructive, 0.16), color: tokens.destructive },
                colorDefault: { backgroundColor: tokens.secondary, color: tokens.secondaryForeground },
            },
        },
        MuiTabs: {
            styleOverrides: { indicator: { backgroundColor: tokens.primary } },
        },
        MuiTableCell: {
            styleOverrides: { root: { borderColor: tokens.border } },
        },
        MuiDivider: { styleOverrides: { root: { borderColor: tokens.border } } },
        MuiDialog: {
            styleOverrides: {
                paper: { border: `1px solid ${tokens.border}`, borderRadius: cardRadius },
            },
        },
    },
});
